// Selector de planes con búsqueda remota (nombre o precio). Debounce de
// 350ms sobre el texto; sin hook de detalle, un value ajeno a las opciones
// cargadas se muestra como #id (fallback).
import { useState } from 'react';
import { Select, Typography } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useT } from '../locales';
import { usePlanesList } from '../features/catalogos/api/useCatalogos';
import type { PlanResponse } from '@aid/shared';
import { formatCurrency } from '../lib/format';
import { useDebouncedValue } from '../lib/useDebouncedValue';

const { Text } = Typography;

const DEBOUNCE_MS = 350;

interface PlanOption extends DefaultOptionType {
  value: number;
  /** Label del select cerrado: nombre + precio. */
  label: string;
  /** Línea principal del dropdown: solo el nombre. */
  nombre?: string;
  /** Línea secundaria del dropdown: precio formateado. */
  secondary?: string;
}

interface PlanSelectProps {
  value?: number | null;
  onChange?: (id: number | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
}

function planOption(p: PlanResponse): PlanOption {
  const nombre = p.nombre ?? `#${p.id}`;
  const precio = p.precio ? formatCurrency(p.precio) : undefined;
  return {
    value: p.id,
    label: `${nombre}${precio ? ` - ${precio}` : ''}`,
    nombre,
    secondary: precio,
    title: `${nombre}${precio ? ` - ${precio}` : ''}`,
  };
}

export function PlanSelect({ value, onChange, disabled, placeholder }: PlanSelectProps) {
  const t = useT();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, DEBOUNCE_MS);

  const { data, isLoading, error } = usePlanesList(debouncedSearch || undefined);

  const planes = data ?? [];
  const enLista = value != null && planes.some((p) => p.id === value);
  const options: PlanOption[] = [
    ...planes.map(planOption),
    ...(value != null && !enLista ? [{ value, label: `#${value}` }] : []),
  ];

  return (
    <Select
      style={{ width: '100%' }}
      showSearch={{ filterOption: false }}
      loading={isLoading}
      disabled={disabled}
      allowClear
      placeholder={placeholder ?? t('common.buscarPlan')}
      defaultActiveFirstOption={false}
      notFoundContent={
        isLoading ? t('common.loading') : error ? t('common.error') : t('common.noResults')
      }
      value={value}
      onChange={onChange}
      onSearch={setSearch}
      options={options}
      optionRender={(option) => {
        const data = option.data as PlanOption;
        return (
          <div>
            <div>{data.nombre ?? data.label}</div>
            {data.secondary && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {data.secondary}
              </Text>
            )}
          </div>
        );
      }}
    />
  );
}
